import { parseDate } from '@/core/lib/date';
import { invertCapitalize } from '@/core/lib/string';

const SETTING_DATE_FORMAT = "yyyy-MM-dd'T'HH:mm:ss";
const SINGAPORE_UTC_OFFSET_HOURS = 8;

export const toEnable = (input: string | undefined) => input?.toLocaleLowerCase() === 'y';

export const toArray = (input: string | undefined, splitter = ',') => input?.split(splitter) ?? [];

export const toFloat = (input: string | undefined) => {
    const result = parseFloat(input ?? '');
    if (Number.isNaN(result)) {
        throw new Error(`Failed to parse float from ${input}`);
    }
    return result;
};

export const toInteger = (input: string | undefined) => {
    const result = parseInt(input ?? '');
    if (Number.isNaN(result)) {
        throw new Error(`Failed to parse integer from ${input}`);
    }
    return result;
};

export const toTrimmedArray = (input: string | undefined, splitter = ','): string[] => toArray(input, splitter)
    .map(x => x.trim())
    .filter(x => x !== '');

export const toNumberArray = (input: string | undefined, splitter = ',') => toArray(input, splitter).map(x => parseInt(x));

export const toEnumArray = <T>(input: string | undefined, splitter = ',') => toArray(input, splitter).map(x => parseInt(x) as unknown as T);

export const toObject = <T, D = T>(input: string | undefined, defaultValue: D): T | D => {
    if (!input) return defaultValue;

    try {
        return JSON.parse(input, (_, value) => {
            if (value && typeof value === 'object' && !Array.isArray(value)) {
                return Object.keys(value).reduce((newObj: Record<string, any>, k) => {
                    newObj[invertCapitalize(k)] = value[k];
                    return newObj;
                }, {});
            }
            return value;
        }) as unknown as T;
    } catch (e) {
        console.error(e);
        return defaultValue;
    }
};

export const expiredDateToEnable = (date: string | undefined) => {
    if (!date) return false;

    const now = new Date();
    return now < parseDate(date, SETTING_DATE_FORMAT, SINGAPORE_UTC_OFFSET_HOURS);
};

export interface IPromotionPeriod {
    start: Date;
    end: Date;
}

// ISO-8601 with an explicit offset (or Z), so the moment never depends on the browser's time zone.
const ISO_TIMESTAMP_WITH_OFFSET = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;

const toIsoDateOrNull = (value: unknown): Date | null => {
    if (typeof value !== 'string' || !ISO_TIMESTAMP_WITH_OFFSET.test(value.trim())) return null;

    const date = new Date(value.trim());
    return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Parses a `{ "start": ISO-8601, "end": ISO-8601 }` setting. Anything invalid or incomplete means
 * "no period", which keeps the promotion hidden (fail closed) without logging.
 */
export const toPromotionPeriodOrNull = (input: string | undefined): IPromotionPeriod | null => {
    let value: unknown;
    try {
        value = JSON.parse(input ?? '');
    } catch {
        return null;
    }
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;

    const { start, end } = value as Record<string, unknown>;
    const startDate = toIsoDateOrNull(start);
    const endDate = toIsoDateOrNull(end);
    return startDate && endDate ? { start: startDate, end: endDate } : null;
};
